import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  FormFeedback,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import GetTokenApi from "../../pages/KeyCloak/GetTokenApi";
import CheckPermission from "../../pages/Auth/CheckPermission";
import api from "../../api/api";
import { useDragScroll } from "../../hooks/useDragScroll";
import "./ModalObjectDetails.css";
import ModalConfirmation from "../ModalConfirmation/ModalConfirmation";
import ModalEditTreeHight from "../ModalEditTreeHeight/ModalEditTreeHeight";
import CroppedImageWithFallback from "../CroppedImageWithFallback/CroppedImageWithFallback";
import ObjectDetailsGroundInfo from "../ObjectDetailsGroundInfo/ObjectDetailsGroundInfo";
import ObjectDetailsAerialInfo from "../ObjectDetailsAerialInfo/ObjectDetailsAerialInfo";
import useProjectTypeStore from "../../store/useProjectTypeStore";
import useDetectionStore from "../../store/useDetectionStore";
import { FaPlus } from "react-icons/fa6";
import Skeleton from "react-loading-skeleton";

const ModalObjectDetails = ({
  offCanvas,
  openDetailsModal,
  pointDetailOffcanvas,
  setOffCanvas,
  setViewLightComponents,
  toggleOffCanvas,
  updateMapPoints,
  updateEditedTree,
}) => {
  const [pageNumber, setPageNumber] = useState(0);
  const [lastPage, setLastPage] = useState(false);
  const [userPermission, setUserPermission] = useState(null);
  const [modal, setModal] = useState(false);
  const [totalElements, setTotalElements] = useState();
  const [loading, setLoading] = useState(false);

  const [editTreeModal, setEditTreeModal] = useState(false);
  const [editTreeLoading, setEditTreeLoading] = useState(false);

  const [description, setDescription] = useState(
    pointDetailOffcanvas.point.description || ""
  );
  const [showInput, setShowInput] = useState(
    !!pointDetailOffcanvas.point.description
  );
  const [loadingUpdateDescription, setLoadingUpdateDescription] =
    useState(false);
  const [errorUpdateDescription, setErrorUpdateDescription] = useState("");

  const { listDetections, setListDetections } = useDetectionStore();
  const projectType = useProjectTypeStore((state) => state.projectType);

  const toggleEditTreeModal = () => setEditTreeModal(!editTreeModal);

  const toggle = () => setModal(!modal);

  const [ref] = useDragScroll();

  useEffect(() => {
    if (!userPermission) {
      GetTokenApi().then((res) => {
        setUserPermission(res);
      });
    }
  }, [userPermission]);

  useEffect(() => {
    setListDetections([]);
    setPageNumber(0);
    setDescription(pointDetailOffcanvas.point.description || "");
    setShowInput(!!pointDetailOffcanvas.point.description);
  }, [pointDetailOffcanvas, offCanvas, setListDetections]);

  useEffect(() => {
    const fetchData = async (path) => {
      try {
        setLoading(true);
        const { data } = await api.get(`${path}?page=${pageNumber}&size=5`);

        setTotalElements(data.totalElements);
        setLastPage(data.last);

        if (data.first) {
          setListDetections(data.content);
        } else {
          setListDetections((prev) => [...prev, ...data.content]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    const endpoint =
      projectType === "GROUND"
        ? `/detections-obb/detected-objects/${pointDetailOffcanvas.point.id}`
        : `/publiclightingpoints/${pointDetailOffcanvas.point.id}/detections`;

    fetchData(endpoint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointDetailOffcanvas, offCanvas, pageNumber, projectType]);

  const loadMoreData = () => {
    setPageNumber(pageNumber + 1);
  };

  const deleteObject = async (type) => {
    try {
      await api.delete(
        `/detected-objects/${type}/${pointDetailOffcanvas.point.id}`
      );

      updateMapPoints(pointDetailOffcanvas.point.id, type);
    } catch (e) {
      console.log(e);
    } finally {
      setOffCanvas(false);
      toggle();
    }
  };

  const detectedObjectName = {
    "cable-spacers": "do Espaçador de cabo",
    poles: "do poste",
    trees: "da árvore",
  };

  useEffect(() => {
    if (pointDetailOffcanvas.viewComponents) {
      setViewLightComponents(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointDetailOffcanvas.viewComponents]);

  function closeModal() {
    setViewLightComponents(false);
    toggleOffCanvas();
  }

  async function editTree(tree, treeHeight) {
    const editedTree = { ...tree, height: treeHeight };

    try {
      setEditTreeLoading(true);

      await api.put(`/detected-objects/trees/${tree.id}`, editedTree);

      updateEditedTree(editedTree);
      toggleEditTreeModal();
    } catch (error) {
      console.log(error);
    } finally {
      setEditTreeLoading(false);
    }
  }

  async function updateDescription() {
    setLoadingUpdateDescription(true);
    try {
      const { type, point } = pointDetailOffcanvas;

      const formattedDescription =
        description.trim() === "" ? null : description;

      await api.put(`/detected-objects/${type}/${point.id}`, {
        ...point,
        description: formattedDescription,
      });

      pointDetailOffcanvas.point.description = formattedDescription;
    } catch (error) {
      setErrorUpdateDescription("Erro ao atualizar descrição!");
    } finally {
      setLoadingUpdateDescription(false);
    }
  }

  return (
    <>
      <ModalConfirmation
        toggle={toggle}
        modal={modal}
        text={`Tem certeza que deseja excluir o objeto #${pointDetailOffcanvas.point.id}?`}
        confirmFunction={() => deleteObject(pointDetailOffcanvas.type)}
      />

      {editTreeModal && (
        <ModalEditTreeHight
          toggle={toggleEditTreeModal}
          modal={editTreeModal}
          tree={pointDetailOffcanvas.point}
          editFunction={editTree}
          loading={editTreeLoading}
        />
      )}

      <Offcanvas
        id="modal-object-details"
        backdrop={false}
        direction="end"
        isOpen={offCanvas}
        toggle={toggleOffCanvas}
        style={{
          position: "fixed",
          top: "65px",
        }}
      >
        <OffcanvasHeader toggle={closeModal}>
          Detalhes {detectedObjectName[pointDetailOffcanvas.type]} #
          {pointDetailOffcanvas.point?.id}
        </OffcanvasHeader>
        <OffcanvasBody>
          <Table hover>
            {projectType === "GROUND" ? (
              <ObjectDetailsGroundInfo
                pointDetailOffcanvas={pointDetailOffcanvas}
                toggleEditTreeModal={toggleEditTreeModal}
              />
            ) : (
              <ObjectDetailsAerialInfo
                pointDetailOffcanvas={pointDetailOffcanvas}
              />
            )}
          </Table>

          {projectType === "GROUND" && (
            <>
              <div
                className="d-flex"
                style={{
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Label
                  for="description"
                  onClick={() => setShowInput(!showInput)}
                  style={{
                    cursor: "pointer",
                  }}
                >
                  Descrição
                </Label>

                {showInput ? (
                  <button
                    className="btn btn-success btn-sm remove-btn"
                    onClick={updateDescription}
                  >
                    Salvar
                  </button>
                ) : (
                  <FaPlus
                    onClick={() => setShowInput(true)}
                    style={{
                      cursor: "pointer",
                      fontSize: "1.25rem",
                    }}
                  />
                )}
              </div>

              {showInput && (
                <>
                  {loadingUpdateDescription ? (
                    <Skeleton height={60} style={{ marginBottom: ".5rem" }} />
                  ) : (
                    <>
                      <Input
                        id="description"
                        name="text"
                        type="textarea"
                        style={{ marginBottom: ".5rem", width: "100%" }}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        invalid={!!errorUpdateDescription}
                      />
                      {!!errorUpdateDescription && (
                        <FormFeedback>{errorUpdateDescription}</FormFeedback>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          <Row style={{ marginBottom: "1em" }}>
            {CheckPermission(
              userPermission && userPermission.role ? userPermission.role : "",
              "EMPLOYEE"
            ) ? (
              <Col>
                {pointDetailOffcanvas.type === "poles" &&
                  projectType === "GROUND" && (
                    <button
                      onClick={() => {
                        setViewLightComponents((prev) => !prev);
                      }}
                      className="btn btn-outline-secondary col-12"
                      type="button"
                    >
                      Visualizar componentes
                    </button>
                  )}
              </Col>
            ) : (
              ""
            )}
          </Row>
          <div className="btn-text-container">
            <small>
              {listDetections?.length}/{totalElements} detecções carregadas
            </small>

            <small
              className="btn btn-danger remove-btn"
              onClick={() => toggle()}
            >
              Remover
            </small>
          </div>

          <Row className="pt-0 le-0 ps-0">
            {!loading && listDetections?.length === 0 && (
              <div>Detecções não encontradas!</div>
            )}
            <div
              className={
                projectType === "GROUND"
                  ? "detections-ground-container"
                  : "detections-aerial-container"
              }
              ref={ref}
            >
              {listDetections?.map((imageCropItem, index) => (
                <div
                  key={imageCropItem.id}
                  className={
                    projectType === "GROUND" ? "item-ground" : "item-aerial"
                  }
                  onClick={() => {
                    openDetailsModal(
                      projectType === "GROUND"
                        ? imageCropItem.detectedObjectId
                        : imageCropItem.public_lighting_point_id,
                      listDetections,
                      index,
                      pointDetailOffcanvas.point
                    );
                  }}
                >
                  <Row>
                    <CroppedImageWithFallback
                      imageCropItem={imageCropItem}
                      projectType={projectType}
                    />
                  </Row>
                </div>
              ))}

              {loading && (
                <Spinner
                  color="primary"
                  style={{
                    margin: "auto",
                    width: "100px",
                    height: "100px",
                  }}
                >
                  Loading...
                </Spinner>
              )}
            </div>
          </Row>

          <Row
            className="m-0 bg-white"
            style={{
              position: "absolute",
              bottom: "0px",
              left: "0px",
              paddingLeft: "10px",
              paddingRight: "10px",
              paddingBottom: "40px",
              zIndex: "500",
              width: "100%",
            }}
          >
            <Button
              outline
              disabled={lastPage}
              className={"btn btn-outline-secondary"}
              onClick={() => {
                loadMoreData();
              }}
            >
              Mostrar mais...
            </Button>
          </Row>
        </OffcanvasBody>
      </Offcanvas>
    </>
  );
};

export default ModalObjectDetails;
